import { useMemo } from 'react';
import { ProjectId } from 'api/models';
import { ProjectPhase } from 'api/project';
import { StatusGroup, FieldValueType, FieldValue } from 'api/tasks';
import { Task } from 'data/tasks';
import { useTasksFromStore } from 'data/tasks/hooks/use-tasks-from-store';
import { useProjectDetail } from './use-project-detail';

export type TaskCounterProp = {
  completed: number;
  todo: number;
  inProgress: number;
  blocked: number;
};

type ProjectPhaseStoreResponse = {
  projectPhase?: ProjectPhase;
  tasks: Task[];
  allTasks: Task[];
  completedTaskCount: number;
  inprogressTaskCount: number;
  firstLevelTaskCounter: TaskCounterProp;
  subTaskCounter: TaskCounterProp;
};
const getTaskCounter = (
  fields: FieldValue<FieldValueType>[] = [],
): TaskCounterProp => ({
  completed:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.Completed,
    )?.length || 0,
  inProgress:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.InProgress,
    )?.length || 0,
  todo:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.Todo,
    )?.length || 0,
  blocked:
    fields.filter(
      (subTaskStatus) =>
        subTaskStatus.metaFieldValue?.group === StatusGroup.Blocked,
    )?.length || 0,
});

export const useProjectPhaseStore = (
  projectId: number,
  phaseId: number,
  taskFilterFunction: (task: Task) => boolean,
): ProjectPhaseStoreResponse => {
  const { data } = useProjectDetail(ProjectId(projectId));
  const projectPhase = useMemo(
    () =>
      data
        ? data.projectPhases.find(
            ({ projectPhaseId }) => phaseId === projectPhaseId,
          )
        : undefined,
    [data, phaseId],
  );
  const projectTasks = useTasksFromStore(ProjectId(projectId));
  const matchingPhaseTasks = useMemo(
    () =>
      projectTasks.filter((task: Task) => {
        return (
          task.projectPhase?.projectPhaseId === phaseId &&
          taskFilterFunction(task)
        );
      }),
    [phaseId, projectTasks, taskFilterFunction],
  );
  const firstLevelTasks = useMemo(
    () =>
      matchingPhaseTasks.filter((task: Task) => {
        return task.parentTaskObject?.taskId == null;
      }),
    [matchingPhaseTasks],
  );
  const firstLevelTasksFields = useMemo(
    () =>
      firstLevelTasks.reduce(
        (result, item) => result.concat(item.fields),
        [] as FieldValue<FieldValueType>[],
      ),
    [firstLevelTasks],
  );
  const subTasksFields = useMemo(
    () =>
      matchingPhaseTasks
        .filter((task: Task) => task.parentTaskObject?.taskId != null)
        .reduce(
          (result, item) => result.concat(item.fields),
          [] as FieldValue<FieldValueType>[],
        ),
    [matchingPhaseTasks],
  );
  const firstLevelTaskCounter = useMemo(
    () => getTaskCounter(firstLevelTasksFields),
    [firstLevelTasksFields],
  );
  const subTaskCounter = useMemo(
    () => getTaskCounter(subTasksFields),
    [subTasksFields],
  );

  return useMemo(
    () => ({
      projectPhase,
      tasks: firstLevelTasks,
      allTasks: matchingPhaseTasks,
      completedTaskCount:
        firstLevelTaskCounter.completed + subTaskCounter.completed,
      inprogressTaskCount:
        firstLevelTaskCounter.inProgress + subTaskCounter.inProgress,
      firstLevelTaskCounter,
      subTaskCounter,
    }),
    [
      firstLevelTaskCounter,
      firstLevelTasks,
      matchingPhaseTasks,
      projectPhase,
      subTaskCounter,
    ],
  );
};

export const useProjectPhaseByProjectId = (
  projectId: number,
): ProjectPhase[] => {
  const { data } = useProjectDetail(ProjectId(projectId));

  return useMemo(() => {
    return data ? data.projectPhases : [];
  }, [data]);
};
